import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import LearnerClient from '../data/api/LearnerClient';
import Loader from '../../../shared/components/Loader';
import Alert from '../../../shared/components/Alert';
import { useLearnerContext } from '../data/context/LearnerContext';
import { getResumePreviewUrl, getResumeDownloadUrl } from '../../../services/resumeFileService';
import ResumeViewer, { ResumeZoomControls, ResumeViewerHandle } from '../../../shared/components/ResumeViewer';
import axios from 'axios';
import '../styles/LearnerStyles.css';
import { Country, State as StateCity } from 'country-state-city';
import Select from 'react-select';
import { Award, Briefcase } from 'lucide-react';
import DateTimePicker from '../../../shared/components/DateTimePicker';
import strings from '../../../utils/Strings';

const GENDER_OPTIONS = [
  { label: 'Prefer Not To Say', value: 'Prefer Not To Say' },
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
  { label: 'Others', value: 'Others' },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EDUCATION_OPTIONS = [
  { label: 'Select Qualification', value: '' },
  { label: 'Under Graduate (UG)', value: 'UG' },
  { label: 'Post Graduate (PG)', value: 'PG' },
];

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    border: state.isFocused ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    color: 'var(--text-heading)',
    background: 'var(--surface)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--primary-ghost)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--primary)' : 'var(--border)'
    },
    outline: 'none',
    transition: 'border-color var(--transition), box-shadow var(--transition)',
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 12px' }),
  singleValue: (base: any, state: any) => ({ ...base, color: state.isDisabled ? 'var(--text-muted)' : 'var(--text-heading)' }),
  input: (base: any) => ({
    ...base,
    color: 'var(--text-heading)',
    margin: '0',
    padding: '0',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    minHeight: 'auto',
    height: 'auto',
    'input:focus': {
      boxShadow: 'none',
      border: 'none',
      outline: 'none'
    }
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({ ...base, padding: '4px' }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, { isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isSelected ? 'var(--primary)' : isFocused ? 'var(--primary-light)' : 'var(--surface)',
    color: isSelected ? '#fff' : 'var(--text-heading)',
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--primary)',
      color: '#fff'
    }
  })
};

interface EditLearnerFormData {
  first_name: string;
  last_name: string;
  email_address: string;
  phone: string;
  status: string;
  gender: string;
  education: string;
  date_of_birth: string;
  resumeFile: File | null;
  resumeKey: string;
  skills: string[];
  job_categories: string[];
  country: string;
  state: string;
  work_preference: string;
  professional_certifications: string[];
  expected_ctc: string;
  year_of_experience: number | '';
  // Identity & legal
  passport_details: { passport_number: string; expiry_date: string; document_url: string };
  drivers_license_details: { license_number: string; expiry_date: string; document_url: string };
  visa_details: { visa_type: string; expiry_date: string; document_url: string };
  aadhaar_details: { aadhaar_number: string; document_url: string };
  pan_card_details: { pan_number: string; document_url: string };
  ssn_details: { ssn: string; document_url: string };
  // Financial
  bank_account_details: { bank_name: string; account_number: string; routing_number: string; account_type: string; branch_name: string; ifsc_code: string; document_url: string };
  // Admin
  note: string;
  onboarded: boolean;
  docFiles: Record<string, File | null>;
  errors: Record<string, string>;
}

const DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png';
const MAX_DOCUMENT_SIZE_MB = 5;
const MAX_DOCUMENT_SIZE_BYTES = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;

const EDIT_DOC_FIELDS: Record<string, keyof EditLearnerFormData> = {
  passport: 'passport_details',
  drivers_license: 'drivers_license_details',
  visa: 'visa_details',
  aadhaar: 'aadhaar_details',
  pan: 'pan_card_details',
  ssn: 'ssn_details',
  bank: 'bank_account_details',
};

const formatSSN = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
};

const EditDocField: React.FC<{
  id: string;
  label: string;
  url: string;
  file: File | null;
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onView: () => void;
  disabled: boolean;
}> = ({ id, label, url, file, onSelect, onClear, onView, disabled }) => (
  <>
    <label>{label}</label>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>

      {file ? (
        <div className="al-resume-picker">
          {!disabled && (
            <>
              <input type="file" id={id} accept={DOC_ACCEPT} style={{ display: 'none' }} onChange={onSelect} />
              <label htmlFor={id} className="al-resume-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '11px' }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Change File
              </label>
            </>
          )}
          <div className="al-resume-info">
            <svg width="14" height="14" fill="none" stroke="#059669" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{file.name}</span>
            {!disabled && (
              <button type="button" className="al-resume-clear" onClick={onClear} title="Remove file">
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      ) : url ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
          <button type="button" onClick={onView} style={{
            flex: 1, height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-full)',
            color: 'var(--primary)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
          }}>
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Document
          </button>

          {!disabled && (
            <>
              <input type="file" id={id} accept={DOC_ACCEPT} style={{ display: 'none' }} onChange={onSelect} />
              <label htmlFor={id} style={{
                width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: '50%', color: 'var(--primary)',
                cursor: 'pointer', transition: 'all 0.2s'
              }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </label>
            </>
          )}
        </div>
      ) : (
        <>
          <input type="file" id={id} accept={DOC_ACCEPT} style={{ display: 'none' }} onChange={onSelect} disabled={disabled} />
          <label htmlFor={id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', height: '42px', width: '100%',
            background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-full)',
            color: 'var(--primary)', fontSize: '13px', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
            opacity: disabled ? 0.6 : 1
          }}>
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            Upload Document
          </label>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>PDF/JPG/PNG up to {MAX_DOCUMENT_SIZE_MB}MB</span>
        </>
      )}
    </div>
  </>
);

const EditableTagInput: React.FC<{
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
}> = ({ label, items, onChange, disabled, placeholder, fullWidth = true }) => {
  const [inputVal, setInputVal] = useState('');

  const addItem = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    if (items.includes(trimmed)) return;
    onChange([...items, trimmed]);
    setInputVal('');
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); addItem(); }
  };

  return (
    <div className={`al-field ${fullWidth ? 'al-field-full' : ''}`.trim()} style={{ display: 'flex', flexDirection: 'column', height: '100%', marginBottom: 0 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
        {label.toLowerCase() === 'skills' ? <Award size={14} /> : <Briefcase size={14} />} {label}
      </label>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '6px 12px',
          background: disabled ? '#f8fafc' : '#fff',
          borderRadius: '8px',
          border: '1px solid #cbd5e1',
          minHeight: '44px',
          height: '100%',
          flex: 1,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          cursor: disabled ? 'not-allowed' : 'text',
          transition: 'border-color var(--transition), box-shadow var(--transition)'
        }}
        onClick={(e) => {
          if (!disabled && e.target === e.currentTarget) {
            document.getElementById(`tag-input-${label.replace(/\\s+/g, '')}`)?.focus();
          }
        }}
        className={!disabled ? "tag-input-container" : ""}
      >
        {items && items.length > 0 ? (
          items.map((item, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: disabled ? '#e2e8f0' : 'rgba(5, 150, 105, 0.1)', color: disabled ? '#64748b' : 'var(--primary)', border: disabled ? '1px solid #cbd5e1' : '1px solid rgba(5, 150, 105, 0.2)', borderRadius: '6px', fontSize: '13px', fontWeight: 500 }}>
              {item}
              {!disabled && (
                <button type="button" onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0, fontSize: '17px', transform: 'translateY(-1px)' }} title={`Remove ${item}`}>
                  ×
                </button>
              )}
            </span>
          ))
        ) : disabled ? (
          <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>None added</span>
        ) : null}

        {!disabled && (
          <input
            id={`tag-input-${label.replace(/\s+/g, '')}`}
            className="tag-input-field"
            type="text"
            placeholder={items.length === 0 ? (placeholder || `Type and press Enter to add...`) : "Type to add more..."}
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1,
              minWidth: '140px',
              padding: '4px',
              border: 'none',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              outline: 'none',
              background: 'transparent',
              color: 'var(--text-heading)'
            }}
          />
        )}
      </div>
      {!disabled && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '6px' }}>
          Press <kbd style={{ background: 'var(--surface-alt)', padding: '2px 6px', borderRadius: '4px', border: '1px solid var(--border)', fontSize: '0.7rem', fontFamily: 'monospace' }}>Enter</kbd> to add {label.toLowerCase()}.
        </div>
      )}
      <style>{`
        .tag-input-container:focus-within {
          border-color: var(--primary) !important;
          box-shadow: 0 0 0 3px var(--primary-ghost) !important;
        }
      `}</style>
    </div>
  );
};

const formatDob = (raw: string, prev: string): string => {
  const digits = raw.replace(/\D/g, '');
  let out = '';
  if (digits.length <= 2) {
    out = digits;
  } else if (digits.length <= 4) {
    out = digits.slice(0, 2) + '/' + digits.slice(2);
  } else {
    out = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4, 8);
  }
  return out;
};

const validateLearner = (l: EditLearnerFormData): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!l.first_name.trim()) errors.first_name = 'First name is required';
  if (!l.last_name.trim()) errors.last_name = 'Last name is required';
  if (!l.email_address.trim()) {
    errors.email_address = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(l.email_address)) {
    errors.email_address = 'Enter a valid email address';
  }
  if (l.date_of_birth && l.date_of_birth.length !== 10) {
    errors.date_of_birth = 'Use format DD/MM/YYYY';
  }
  return errors;
};

const EditLearnerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const isViewMode = location.pathname.includes('/view');
  const { dispatch } = useLearnerContext();

  const [form, setForm] = useState<EditLearnerFormData | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [uploadingResumes, setUploadingResumes] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [approving, setApproving] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [approvedAlert, setApprovedAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [resumePreviewUrl, setResumePreviewUrl] = useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const activePreviewUrlRef = useRef<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [resumeZoom, setResumeZoom] = useState(1.5);
  const resumeViewerRef = useRef<ResumeViewerHandle>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activeTab, setActiveTab] = useState<'personal' | 'professional' | 'documents' | 'financials' | 'notes'>('personal');
  const [isEducationDropdown, setIsEducationDropdown] = useState(true);

  const closePreview = () => {
    setIsPreviewModalOpen(false);
    setResumePreviewUrl(null);
    setPdfLoading(false);
    activePreviewUrlRef.current = null;
  };

  const openResumePreview = async (url: string | null | undefined) => {
    setIsPreviewModalOpen(true);
    setPdfLoading(true);
    setResumePreviewUrl(null);
    activePreviewUrlRef.current = url || null;

    const inlineUrl = await getResumePreviewUrl(url);
    if (activePreviewUrlRef.current === (url || null)) {
      setResumePreviewUrl(inlineUrl);
    }
  };

  useEffect(() => {
    if (!email) return;
    const fetchLearner = async () => {
      try {
        setLoading(true);
        const data = await LearnerClient.get(email);
        const loadedStatus = data.status || 'Inactive';
        setCurrentStatus(loadedStatus);
        setForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email_address: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.email_address || "") ? "" : (data.email_address || ''),
          status: loadedStatus,
          gender: data.gender || 'Prefer Not To Say',
          education: data.education || '',
          phone: data.phone || '',
          date_of_birth: data.date_of_birth || '',
          resumeFile: null,
          resumeKey: data.resume_url || '',
          skills: data.skills || [],
          job_categories: data.job_categories || [],
          country: data.country || '',
          state: data.state || '',
          work_preference: data.work_preference || '',
          professional_certifications: data.professional_certifications || [],
          expected_ctc: data.expected_ctc || '',
          year_of_experience: data.year_of_experience !== undefined && data.year_of_experience !== null ? Number(data.year_of_experience) : '',
          passport_details: { passport_number: data.passport_details?.passport_number || '', expiry_date: data.passport_details?.expiry_date || '', document_url: data.passport_details?.document_url || '' },
          drivers_license_details: { license_number: data.drivers_license_details?.license_number || '', expiry_date: data.drivers_license_details?.expiry_date || '', document_url: data.drivers_license_details?.document_url || '' },
          visa_details: { visa_type: data.visa_details?.visa_type || '', expiry_date: data.visa_details?.expiry_date || '', document_url: data.visa_details?.document_url || '' },
          aadhaar_details: { aadhaar_number: data.aadhaar_details?.aadhaar_number || '', document_url: data.aadhaar_details?.document_url || '' },
          pan_card_details: { pan_number: data.pan_card_details?.pan_number || '', document_url: data.pan_card_details?.document_url || '' },
          ssn_details: { ssn: data.ssn_details?.ssn || '', document_url: data.ssn_details?.document_url || '' },
          bank_account_details: { bank_name: data.bank_account_details?.bank_name || '', account_number: data.bank_account_details?.account_number || '', routing_number: data.bank_account_details?.routing_number || '', account_type: data.bank_account_details?.account_type || '', branch_name: data.bank_account_details?.branch_name || '', ifsc_code: data.bank_account_details?.ifsc_code || '', document_url: data.bank_account_details?.document_url || '' },
          note: data.note || '',
          onboarded: data.onboarded === true,
          docFiles: { passport: null, drivers_license: null, visa: null, aadhaar: null, pan: null, ssn: null, bank: null },
          errors: {},
        });
        if (data.education && !strings.learner.degreesList.includes(data.education)) {
          setIsEducationDropdown(false);
        }
      } catch (err: any) {
        setErrorMsg('Failed to load learner details.');
      } finally {
        setLoading(false);
      }
    };
    fetchLearner();
  }, [email]);

  const update = useCallback((field: keyof EditLearnerFormData, value: any) => {
    setForm(prev => prev ? { ...prev, [field]: value, errors: { ...prev.errors, [field]: '' } } : null);
  }, []);

  const handleDob = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!form) return;
    const raw = e.target.value;
    const formatted = formatDob(raw, form.date_of_birth);
    setForm(prev => prev ? { ...prev, date_of_birth: formatted, errors: { ...prev.errors, date_of_birth: '' } } : null);
  }, [form]);

  const handleDobKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  }, []);

  const handleResumeSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setForm(prev => prev ? { ...prev, resumeFile: file, resumeKey: '' } : null);
  }, []);

  const handleDocSelect = useCallback((key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(pdf|jpg|jpeg|png)$/)) {
      setErrorMsg('Document must be a PDF, JPG, or PNG file.');
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      setErrorMsg(`Document exceeds the ${MAX_DOCUMENT_SIZE_MB}MB size limit.`);
      return;
    }
    setForm(prev => prev ? { ...prev, docFiles: { ...prev.docFiles, [key]: file } } : null);
  }, []);

  const handleDocView = async (url: string) => {
    await openResumePreview(url);
  };

  const uploadResume = async (file: File): Promise<string> => {
    const data = await LearnerClient.createUploadUrl(file.name);
    const { uploadURL, key, downloadURL } = data;
    await axios.put(uploadURL, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    return downloadURL || key;
  };

  const handleSave = async () => {
    if (!form || !email) return;

    const errors = validateLearner(form);
    if (Object.keys(errors).length > 0) {
      setForm({ ...form, errors });
      const errorMessages = Object.values(errors).join('; ');
      setErrorMsg(`Please fix the validation errors in the form before saving: ${errorMessages}`);
      return;
    }

    try {
      setSaving(true);
      let finalResumeKey = form.resumeKey;

      if (form.resumeFile) {
        setUploadingResumes(true);
        finalResumeKey = await uploadResume(form.resumeFile);
        setUploadingResumes(false);
      }

      const withDocs = { ...form };
      for (const key of Object.keys(EDIT_DOC_FIELDS)) {
        const file = form.docFiles[key];
        if (file) {
          const url = await uploadResume(file);
          const detailKey = EDIT_DOC_FIELDS[key];
          (withDocs as any)[detailKey] = { ...(withDocs[detailKey] as any), document_url: url };
        }
      }

      const payload = {
        first_name: withDocs.first_name.trim(),
        last_name: withDocs.last_name.trim(),
        email_address: withDocs.email_address.trim().toLowerCase(),
        status: withDocs.status,
        gender: withDocs.gender,
        education: withDocs.education || undefined,
        phone: withDocs.phone || undefined,
        date_of_birth: withDocs.date_of_birth || undefined,
        resume_url: finalResumeKey || undefined,
        country: withDocs.country || undefined,
        state: withDocs.state || undefined,
        work_preference: withDocs.work_preference || undefined,
        professional_certifications: withDocs.professional_certifications || undefined,
        expected_ctc: withDocs.expected_ctc || undefined,
        year_of_experience: withDocs.year_of_experience !== '' ? Number(withDocs.year_of_experience) : undefined,
        skills: withDocs.skills || undefined,
        job_categories: withDocs.job_categories || undefined,
        passport_details: withDocs.passport_details,
        drivers_license_details: withDocs.drivers_license_details,
        visa_details: withDocs.visa_details,
        aadhaar_details: withDocs.aadhaar_details,
        pan_card_details: withDocs.pan_card_details,
        ssn_details: withDocs.ssn_details,
        bank_account_details: withDocs.bank_account_details,
        note: withDocs.note || undefined,
      };

      await LearnerClient.update(email, payload);
      dispatch({ type: 'UPDATE_LEARNER', payload: { ...payload, id: email } as any });
      setSuccessAlert(true);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || 'Failed to update learner.');
    } finally {
      setSaving(false);
      setUploadingResumes(false);
    }
  };

  const handleOnboard = async () => {
    if (!email || !form) return;
    try {
      setOnboarding(true);
      await LearnerClient.onboard(email);
      setForm(prev => prev ? { ...prev, onboarded: true } : null);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || 'Failed to onboard applicant.');
    } finally {
      setOnboarding(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleApprove = async () => {
    if (!email) return;
    setApproving(true);
    try {
      await LearnerClient.approveUsers([email]);
      setCurrentStatus('Active');
      update('status', 'Active');
      setApprovedAlert(true);
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.error || err?.response?.data?.detail || 'Failed to approve applicant.',
      );
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return <Loader isLoading={true} message="Loading applicant details..." />;
  }

  if (!form) {
    return (
      <div className="learner-page">
        <div className="table-empty">Applicant not found</div>
      </div>
    );
  }

  return (
    <>
      <Loader
        isLoading={saving}
        message={uploadingResumes ? 'Uploading resume...' : 'Saving learner...'}
      />
      <Alert
        show={successAlert}
        title="Applicant Updated!"
        message="The applicant information has been successfully updated."
        type="success"
        primaryButtonText="Go to Applicant List"
        onClose={() => { setSuccessAlert(false); navigate('/dashboard/learners'); }}
      />
      <Alert
        show={!!errorMsg}
        title="Error"
        message={errorMsg || ''}
        type="error"
        primaryButtonText="OK"
        onClose={() => setErrorMsg(null)}
      />
      <Alert
        show={approvedAlert}
        title="Applicant Approved!"
        message="The applicant has been approved successfully."
        type="success"
        primaryButtonText="Go to Applicant List"
        onClose={() => { setApprovedAlert(false); navigate('/dashboard/learners'); }}
      />

      <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>

        {/* ── Hero ── */}
        <div className="lp-hero" style={{ flexDirection: 'column', gap: '4px', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div className="lp-hero-left">
              <button className="up-back-btn" onClick={() => navigate('/dashboard/learners')} title="Back">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m7-7l-7 7 7 7" />
                </svg>
              </button>
              <h1>{isViewMode ? 'View Applicant' : 'Edit Applicant'}</h1>
              <span className="lp-hero-chip">{isViewMode ? 'View candidate details' : 'Update candidate details'}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Status Toggle */}
              <div className="al-status-toggle-wrap" style={{ opacity: isViewMode ? 0.7 : 1, pointerEvents: isViewMode ? 'none' : 'auto' }}>
                <span className={`al-status-label ${form.status === 'Active' ? 'active' : 'inactive'}`}>
                  {form.status}
                </span>
                <button
                  type="button"
                  className={`al-toggle ${form.status === 'Active' ? 'on' : 'off'}`}
                  onClick={() => update('status', form.status === 'Active' ? 'Inactive' : 'Active')}
                  title={`Toggle status (currently ${form.status})`}
                  disabled={isViewMode}
                >
                  <span className="al-toggle-knob" />
                </button>
              </div>

              {currentStatus === 'Active' && (form.onboarded ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '8px 18px', background: 'rgba(34, 197, 94, 0.1)', color: '#16a34a',
                  borderRadius: 'var(--radius-full)', fontSize: '13px', fontWeight: 600,
                  border: '1px solid rgba(34, 197, 94, 0.2)'
                }}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Onboarded
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleOnboard}
                  disabled={onboarding}
                  className="al-onboard-btn"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '9px 20px',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    color: '#fff', border: 'none', borderRadius: 'var(--radius-full)',
                    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    opacity: onboarding ? 0.6 : 1,
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!onboarding) {
                      (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(22, 163, 74, 0.3)';
                      (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!onboarding) {
                      (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(22, 163, 74, 0.2)';
                      (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {onboarding ? (
                    <svg width="14" height="14" className="al-spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  ) : (
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                  )}
                  {onboarding ? 'Sending Welcome Email…' : 'Onboard Applicant'}
                </button>
              ))}
              {isViewMode && (
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/learners/edit`, { state: { email } })}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '9px 20px',
                    background: 'linear-gradient(135deg, #fb923c, #f97316)',
                    color: '#fff', border: 'none', borderRadius: 'var(--radius-full)',
                    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.3)';
                    (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.2)';
                    (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                  }}
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  Edit Applicant
                </button>
              )}
            </div>

          </div>
        </div>

        <div className="al-tabs">
          <button type="button" className={`al-tab ${activeTab === 'personal' ? 'active' : ''}`} onClick={() => setActiveTab('personal')}>Personal Info</button>
          <button type="button" className={`al-tab ${activeTab === 'professional' ? 'active' : ''}`} onClick={() => setActiveTab('professional')}>Professional Details</button>
          <button type="button" className={`al-tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>Documents</button>
          <button type="button" className={`al-tab ${activeTab === 'financials' ? 'active' : ''}`} onClick={() => setActiveTab('financials')}>Financials</button>
          <button type="button" className={`al-tab ${activeTab === 'notes' ? 'active' : ''}`} onClick={() => setActiveTab('notes')}>Notes</button>
        </div>

        <div className="al-cards-list">
          {activeTab === 'personal' && (
            <div className="al-card">
              <div className="al-card-body">
                <div className="al-form-grid">

                  <div className={`al-field ${form.errors.first_name ? 'has-error' : ''}`}>
                    <label>First Name {!isViewMode && <span className="req">*</span>}</label>
                    <input
                      type="text"
                      placeholder="e.g. Priya"
                      value={form.first_name}
                      onChange={e => update('first_name', e.target.value)}
                      disabled={isViewMode}
                    />
                    {form.errors.first_name && <span className="al-error">{form.errors.first_name}</span>}
                  </div>

                  <div className={`al-field ${form.errors.last_name ? 'has-error' : ''}`}>
                    <label>Last Name {!isViewMode && <span className="req">*</span>}</label>
                    <input
                      type="text"
                      placeholder="e.g. Sharma"
                      value={form.last_name}
                      onChange={e => update('last_name', e.target.value)}
                      disabled={isViewMode}
                    />
                    {form.errors.last_name && <span className="al-error">{form.errors.last_name}</span>}
                  </div>

                  <div className={`al-field ${form.errors.email_address ? 'has-error' : ''}`}>
                    <label>Email Address {!isViewMode && <span className="req">*</span>}</label>
                    <input
                      type="email"
                      placeholder="e.g. priya@email.com"
                      value={form.email_address}
                      onChange={e => update('email_address', e.target.value)}
                      disabled={isViewMode}
                    />
                    {form.errors.email_address && <span className="al-error">{form.errors.email_address}</span>}
                  </div>

                  <div className="al-field">
                    <label>Gender</label>
                    <select value={form.gender} onChange={e => update('gender', e.target.value)} disabled={isViewMode}>
                      {GENDER_OPTIONS.map(g => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="al-field">
                    <label>Phone Number</label>
                    <input
                      type="text"
                      placeholder="e.g. +91 9876543210"
                      value={form.phone}
                      onChange={e => update('phone', e.target.value)}
                      disabled={isViewMode}
                    />
                  </div>

                  <div className="al-field">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ margin: 0 }}>Educational Qualification</label>
                      {!isViewMode && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsEducationDropdown(!isEducationDropdown);
                            if (!isEducationDropdown && !strings.learner.degreesList.includes(form.education)) {
                              update('education', '');
                            }
                          }}
                          style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '11px', cursor: 'pointer', padding: 0, fontWeight: 600, textDecoration: 'underline' }}
                        >
                          {isEducationDropdown ? 'Enter Manually' : 'Select from List'}
                        </button>
                      )}
                    </div>
                    {isEducationDropdown ? (
                      <Select
                        options={strings.learner.degreesList.map(deg => ({ value: deg, label: deg }))}
                        value={strings.learner.degreesList.includes(form.education) ? { value: form.education, label: form.education } : null}
                        onChange={(val: any) => update('education', val?.value || '')}
                        isDisabled={isViewMode}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition={'fixed'}
                        placeholder="Select Qualification"
                      />
                    ) : (
                      <input
                        type="text"
                        placeholder="e.g. B.Tech Computer Science"
                        value={form.education}
                        onChange={e => update('education', e.target.value)}
                        disabled={isViewMode}
                      />
                    )}
                  </div>

                  <div className={`al-field ${form.errors.date_of_birth ? 'has-error' : ''}`}>
                    <label>Date of Birth</label>
                    <input
                      type="text"
                      placeholder="DD/MM/YYYY"
                      value={form.date_of_birth}
                      onChange={handleDob}
                      onKeyDown={handleDobKeyDown}
                      maxLength={10}
                      inputMode="numeric"
                      disabled={isViewMode}
                    />
                    {form.errors.date_of_birth && (
                      <span className="al-error">{form.errors.date_of_birth}</span>
                    )}
                  </div>

                  <div className="al-field">
                    <label>Country</label>
                    <Select
                      options={Country.getAllCountries().map(c => ({ value: c.name, label: c.name, code: c.isoCode }))}
                      value={Country.getAllCountries().map(c => ({ value: c.name, label: c.name, code: c.isoCode })).find(o => o.value === form.country) || null}
                      onChange={(val: any) => { update('country', val?.value || ''); update('state', ''); }}
                      isDisabled={isViewMode}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition={'fixed'}
                      placeholder="Select country"
                    />
                  </div>

                  <div className="al-field">
                    <label>State</label>
                    <Select
                      options={(() => {
                        const selectedCountryCode = Country.getAllCountries().find(c => c.name === form.country)?.isoCode;
                        if (!selectedCountryCode) return [];
                        return StateCity.getStatesOfCountry(selectedCountryCode).map(s => ({ value: s.name, label: s.name, code: s.isoCode }));
                      })()}
                      value={(() => {
                        const selectedCountryCode = Country.getAllCountries().find(c => c.name === form.country)?.isoCode;
                        if (!selectedCountryCode) return null;
                        return StateCity.getStatesOfCountry(selectedCountryCode)
                          .map(s => ({ value: s.name, label: s.name, code: s.isoCode }))
                          .find(o => o.value === form.state) || null;
                      })()}
                      onChange={(val: any) => update('state', val?.value || '')}
                      isDisabled={isViewMode || !form.country}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition={'fixed'}
                      placeholder="Select state"
                    />
                  </div>

                  <div className="al-field al-field-full">
                    <label>Resume {!isViewMode && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 400 }}>(PDF only)</span>}</label>
                    <div className="al-resume-picker">
                      {!isViewMode && (
                        <>
                          <input
                            type="file"
                            accept=".pdf"
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                            onChange={handleResumeSelect}
                          />
                          <button
                            type="button"
                            className="al-resume-btn"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            {form.resumeFile || form.resumeKey ? 'Change File' : 'Attach Resume'}
                          </button>
                        </>
                      )}

                      {form.resumeFile && !isViewMode && (
                        <div className="al-resume-info">
                          <svg width="14" height="14" fill="none" stroke="#059669" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>{form.resumeFile.name}</span>
                          <button
                            type="button"
                            className="al-resume-clear"
                            onClick={() => update('resumeFile', null)}
                            title="Remove file"
                          >
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}

                      {!form.resumeFile && form.resumeKey && (
                        <div className="al-resume-info" style={{ cursor: 'pointer' }} onClick={() => { openResumePreview(form.resumeKey); }}>
                          <svg width="14" height="14" fill="none" stroke="#059669" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          <span>View Current Resume</span>
                        </div>
                      )}

                      {!form.resumeFile && !form.resumeKey && isViewMode && <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>No resume attached</span>}
                      {!form.resumeFile && !form.resumeKey && !isViewMode && <span className="al-hint">PDF accepted</span>}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}

          {activeTab === 'professional' && (
            <>
              <div className="al-card">

                <div className="al-card-body">
                  <div className="al-form-grid">
                    <div className="al-field-full" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', paddingBottom: '24px', borderBottom: '1px solid var(--border)', marginBottom: '12px' }}>
                      <div className="al-field">
                        <label>Years of Experience</label>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="e.g. 3.5"
                          value={form.year_of_experience}
                          onChange={e => update('year_of_experience', e.target.value !== '' ? parseFloat(e.target.value) : '')}
                          disabled={isViewMode}
                        />
                      </div>

                      <div className="al-field">
                        <label>Work Preference</label>
                        <select
                          value={form.work_preference}
                          onChange={e => update('work_preference', e.target.value)}
                          disabled={isViewMode}
                          style={{
                            width: '100%',
                            height: '42px',
                            border: '1.5px solid var(--border)',
                            borderRadius: 'var(--radius-md)',
                            padding: '0 12px',
                            fontSize: '0.85rem',
                            color: 'var(--text-heading)',
                            backgroundColor: isViewMode ? 'var(--surface-alt)' : 'var(--surface)',
                            outline: 'none',
                            cursor: isViewMode ? 'not-allowed' : 'pointer',
                            opacity: isViewMode ? 0.7 : 1
                          }}
                        >
                          <option value="">Select</option>
                          <option value="Hybrid">Hybrid</option>
                          <option value="Onsite">Onsite</option>
                          <option value="Remote">Remote</option>
                        </select>
                      </div>

                      <div className="al-field">
                        <label>Expected CTC</label>
                        <input
                          type="text"
                          value={form.expected_ctc}
                          onChange={e => update('expected_ctc', e.target.value)}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>

                    <div className="al-field-full" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                      <EditableTagInput
                        label="Skills"
                        items={form.skills}
                        onChange={(val: string[]) => update('skills', val)}
                        disabled={isViewMode}
                        placeholder="e.g. Python, React..."
                        fullWidth={false}
                      />

                      <EditableTagInput
                        label="Job Categories"
                        items={form.job_categories}
                        onChange={(val: string[]) => update('job_categories', val)}
                        disabled={isViewMode}
                        placeholder="e.g. Frontend Developer..."
                        fullWidth={false}
                      />

                      <EditableTagInput
                        label="Professional Certifications"
                        items={form.professional_certifications}
                        onChange={(val: string[]) => update('professional_certifications', val)}
                        disabled={isViewMode}
                        placeholder="e.g. AWS Certified..."
                        fullWidth={false}
                      />
                    </div>
                  </div>
                </div>
              </div>

            </>
          )}

          {activeTab === 'documents' && (
            <div className="al-card">

              <div className="al-card-body" style={{ padding: '0 24px 24px 24px' }}>
                <div className="al-form-grid" style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>

                  {/* Passport */}
                  <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#7c3aed', marginBottom: '16px' }}>
                      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                      <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>PASSPORT</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>Passport Number</label>
                        <input type="text" placeholder="e.g. P1234567" value={form.passport_details.passport_number} onChange={e => update('passport_details', { ...form.passport_details, passport_number: e.target.value })} disabled={isViewMode} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>Expiry Date</label>
                        <DateTimePicker dateOnly readOnly={isViewMode} placeholder="dd - mm - yyyy" value={form.passport_details.expiry_date} onChange={(val: string) => update('passport_details', { ...form.passport_details, expiry_date: val })} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <EditDocField
                          id="edit-doc-passport"
                          label="Document"
                          url={form.passport_details.document_url}
                          file={form.docFiles.passport}
                          onSelect={e => handleDocSelect('passport', e)}
                          onClear={() => setForm(prev => prev ? { ...prev, docFiles: { ...prev.docFiles, passport: null } } : null)}
                          onView={() => handleDocView(form.passport_details.document_url)}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Driver's License */}
                  <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#059669', marginBottom: '16px' }}>
                      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>DRIVER'S LICENSE</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>License Number</label>
                        <input type="text" placeholder="e.g. DL123456" value={form.drivers_license_details.license_number} onChange={e => update('drivers_license_details', { ...form.drivers_license_details, license_number: e.target.value })} disabled={isViewMode} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>Expiry Date</label>
                        <DateTimePicker dateOnly readOnly={isViewMode} placeholder="dd - mm - yyyy" value={form.drivers_license_details.expiry_date} onChange={(val: string) => update('drivers_license_details', { ...form.drivers_license_details, expiry_date: val })} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <EditDocField
                          id="edit-doc-drivers-license"
                          label="Document"
                          url={form.drivers_license_details.document_url}
                          file={form.docFiles.drivers_license}
                          onSelect={e => handleDocSelect('drivers_license', e)}
                          onClear={() => setForm(prev => prev ? { ...prev, docFiles: { ...prev.docFiles, drivers_license: null } } : null)}
                          onView={() => handleDocView(form.drivers_license_details.document_url)}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Visa */}
                  <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a', marginBottom: '16px' }}>
                      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>VISA</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>Visa Type / Status</label>
                        <input type="text" placeholder="e.g. H-1B, Student Visa" value={form.visa_details.visa_type} onChange={e => update('visa_details', { ...form.visa_details, visa_type: e.target.value })} disabled={isViewMode} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>Expiry Date</label>
                        <DateTimePicker dateOnly readOnly={isViewMode} placeholder="dd - mm - yyyy" value={form.visa_details.expiry_date} onChange={(val: string) => update('visa_details', { ...form.visa_details, expiry_date: val })} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <EditDocField
                          id="edit-doc-visa"
                          label="Document"
                          url={form.visa_details.document_url}
                          file={form.docFiles.visa}
                          onSelect={e => handleDocSelect('visa', e)}
                          onClear={() => setForm(prev => prev ? { ...prev, docFiles: { ...prev.docFiles, visa: null } } : null)}
                          onView={() => handleDocView(form.visa_details.document_url)}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Aadhaar */}
                  <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a855f7', marginBottom: '16px' }}>
                      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" /></svg>
                      <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>AADHAAR</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>Aadhaar Number</label>
                        <input type="text" placeholder="XXXX XXXX XXXX" maxLength={14} value={form.aadhaar_details.aadhaar_number} onChange={e => update('aadhaar_details', { ...form.aadhaar_details, aadhaar_number: e.target.value })} disabled={isViewMode} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <EditDocField
                          id="edit-doc-aadhaar"
                          label="Document"
                          url={form.aadhaar_details.document_url}
                          file={form.docFiles.aadhaar}
                          onSelect={e => handleDocSelect('aadhaar', e)}
                          onClear={() => setForm(prev => prev ? { ...prev, docFiles: { ...prev.docFiles, aadhaar: null } } : null)}
                          onView={() => handleDocView(form.aadhaar_details.document_url)}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  </div>

                  {/* PAN Card */}
                  <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#eab308', marginBottom: '16px' }}>
                      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                      <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>PAN CARD</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>PAN Number</label>
                        <input type="text" placeholder="e.g. ABCDE1234F" maxLength={10} value={form.pan_card_details.pan_number} onChange={e => update('pan_card_details', { ...form.pan_card_details, pan_number: e.target.value.toUpperCase() })} disabled={isViewMode} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <EditDocField
                          id="edit-doc-pan"
                          label="Document"
                          url={form.pan_card_details.document_url}
                          file={form.docFiles.pan}
                          onSelect={e => handleDocSelect('pan', e)}
                          onClear={() => setForm(prev => prev ? { ...prev, docFiles: { ...prev.docFiles, pan: null } } : null)}
                          onView={() => handleDocView(form.pan_card_details.document_url)}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  </div>

                  {/* SSN */}
                  <div className="al-field-full" style={{ padding: '24px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', marginBottom: '16px' }}>
                      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>SOCIAL SECURITY NUMBER</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <label>SSN</label>
                        <input type="text" placeholder="XXX-XX-XXXX" maxLength={11} value={form.ssn_details.ssn} onChange={e => update('ssn_details', { ...form.ssn_details, ssn: formatSSN(e.target.value) })} disabled={isViewMode} />
                      </div>
                      <div className="al-field" style={{ marginBottom: 0 }}>
                        <EditDocField
                          id="edit-doc-ssn"
                          label="Document"
                          url={form.ssn_details.document_url}
                          file={form.docFiles.ssn}
                          onSelect={e => handleDocSelect('ssn', e)}
                          onClear={() => setForm(prev => prev ? { ...prev, docFiles: { ...prev.docFiles, ssn: null } } : null)}
                          onView={() => handleDocView(form.ssn_details.document_url)}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}

          {activeTab === 'financials' && (
            <div className="al-card">

              <div className="al-card-body">
                <div className="al-form-grid">
                  <div className="al-field">
                    <label>Bank Name</label>
                    <input type="text" placeholder="e.g. State Bank of India" value={form.bank_account_details.bank_name} onChange={e => update('bank_account_details', { ...form.bank_account_details, bank_name: e.target.value })} disabled={isViewMode} />
                  </div>
                  <div className="al-field">
                    <label>Account Number</label>
                    <input type="text" placeholder="e.g. 1234567890" value={form.bank_account_details.account_number} onChange={e => update('bank_account_details', { ...form.bank_account_details, account_number: e.target.value })} disabled={isViewMode} />
                  </div>
                  <div className="al-field">
                    <label>Routing Number</label>
                    <input type="text" placeholder="e.g. 021000021" value={form.bank_account_details.routing_number} onChange={e => update('bank_account_details', { ...form.bank_account_details, routing_number: e.target.value })} disabled={isViewMode} />
                  </div>
                  <div className="al-field">
                    <label>Branch Address</label>
                    <input type="text" placeholder="e.g. Main Branch" value={form.bank_account_details.branch_name} onChange={e => update('bank_account_details', { ...form.bank_account_details, branch_name: e.target.value })} disabled={isViewMode} />
                  </div>
                  <div className="al-field">
                    <label>IFSC Code</label>
                    <input type="text" placeholder="e.g. SBIN0001234" value={form.bank_account_details.ifsc_code} onChange={e => update('bank_account_details', { ...form.bank_account_details, ifsc_code: e.target.value.toUpperCase() })} disabled={isViewMode} />
                  </div>
                  <div className="al-field">
                    <label>Account Type</label>
                    <select value={form.bank_account_details.account_type} onChange={e => update('bank_account_details', { ...form.bank_account_details, account_type: e.target.value })} disabled={isViewMode}>
                      <option value="">Select type</option>
                      <option value="Savings">Savings</option>
                      <option value="Current">Current</option>
                      <option value="Checking">Checking</option>
                    </select>
                  </div>
                  <div className="al-field">
                    <EditDocField
                      id="edit-doc-bank"
                      label="Bank Document"
                      url={form.bank_account_details.document_url}
                      file={form.docFiles.bank}
                      onSelect={(e) => handleDocSelect('bank', e)}
                      onClear={() => setForm(prev => prev ? { ...prev, docFiles: { ...prev.docFiles, bank: null } } : null)}
                      onView={() => handleDocView(form.bank_account_details.document_url)}
                      disabled={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="al-card" style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface-alt)' }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-heading)', marginBottom: '4px' }}>Admin Notes</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Internal summary or remarks about this applicant (not visible to the applicant).</div>
              </div>
              <div className="al-card-body" style={{ padding: '24px' }}>
                <textarea
                  placeholder="Add any internal notes, observations, or summary about this applicant..."
                  value={form.note}
                  onChange={e => update('note', e.target.value)}
                  disabled={isViewMode}
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border)',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    color: 'var(--text-heading)',
                    background: isViewMode ? 'var(--surface-alt)' : 'var(--surface)',
                    resize: 'vertical',
                    transition: 'border-color var(--transition)',
                    outline: 'none',
                    lineHeight: 1.7,
                  }}
                />
              </div>
            </div>
          )}

        </div>
        <div className="al-actions-bar" style={{ justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-ghost" onClick={() => navigate('/dashboard/learners')}>{isViewMode ? 'Back' : 'Cancel'}</button>
            {!isViewMode && (
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <svg width="15" height="15" className="al-spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Saving…
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            )}
          </div>
        </div>

      </motion.div>


      {/* ✨ Resume PDF Viewer Modal ✨ */}
      {isPreviewModalOpen && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '24px 80px' }}
          onClick={closePreview}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, borderRadius: '20px 20px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '8px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>Document Preview</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Uploaded Document</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ResumeZoomControls
                  zoom={resumeZoom}
                  onZoomChange={setResumeZoom}
                  onSearchClick={() => resumeViewerRef.current?.openSearch()}
                  onDownloadClick={async () => {
                    const downloadUrl = await getResumeDownloadUrl(resumePreviewUrl);
                    if (downloadUrl) {
                      const a = document.createElement("a");
                      a.href = downloadUrl;
                      a.download = "resume";
                      a.click();
                    }
                  }}
                />
                <button
                  onClick={closePreview}
                  style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, padding: '8px 14px', borderRadius: '8px', transition: 'all 0.2s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'; }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> Close
                </button>
              </div>
            </div>
            {/* Body */}
            <div style={{ flex: 1, position: 'relative' }}>
              {pdfLoading && (
                <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', zIndex: 1 }}>
                  <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 500 }}>Loading Document...</span>
                </div>
              )}
              {resumePreviewUrl && (
                <ResumeViewer
                  ref={resumeViewerRef}
                  key={resumePreviewUrl}
                  url={resumePreviewUrl}
                  zoom={resumeZoom}
                  onZoomChange={setResumeZoom}
                  onLoaded={() => setPdfLoading(false)}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    </>
  );
};

export default EditLearnerPage;
